import { useState } from 'react';
import { Bookmark } from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useTemplates } from '@/hooks/useTemplates';
import { QRDesignSettings, QRType } from '@/stores/qrStore';

interface SaveTemplateModalProps {
  qrType: QRType;
  designSettings: QRDesignSettings;
}

export function SaveTemplateModal({ qrType, designSettings }: SaveTemplateModalProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const { saveUserTemplate } = useTemplates();

  const handleSave = async () => {
    if (!name.trim()) return;

    await saveUserTemplate.mutateAsync({
      name: name.trim(),
      description: description.trim() || undefined,
      qr_type: qrType,
      design_settings: {
        dotsColor: designSettings.dotsColor,
        backgroundColor: designSettings.backgroundColor,
        dotsType: designSettings.dotsType,
        cornersSquareType: designSettings.cornersSquareType,
        cornersDotType: designSettings.cornersDotType,
        cornersSquareColor: designSettings.cornersSquareColor,
        cornersDotColor: designSettings.cornersDotColor,
        gradient: designSettings.gradient,
        frame: designSettings.frame,
      },
    });

    setOpen(false);
    setName('');
    setDescription('');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'w-full')}>
        <Bookmark className="h-4 w-4 mr-2" />
        Save as Template
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save as Template</DialogTitle>
          <DialogDescription>
            Save this design as a reusable template for future QR codes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">Template Name</Label>
            <Input
              id="template-name"
              placeholder="e.g., My Business Card"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="template-description">Description (optional)</Label>
            <Textarea
              id="template-description"
              placeholder="Brief description of this template..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!name.trim() || saveUserTemplate.isPending}
          >
            {saveUserTemplate.isPending ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
